// Joana chat shell — the TRUSTED location capability handler (location.coarse /
// location.fine). A mini-app's `sema.location.coarse()/.fine()` is proxied from
// the worker to the main thread; this answers it via the browser Geolocation API.
// The browser owns the permission prompt — an undeclared/denied grant is simply
// unreachable. `coarse` deliberately rounds the fix (~1 km) and floors the
// reported accuracy so a coarse grant can't be used to pin a user precisely;
// `fine` requests high accuracy and returns the raw fix.
//
// DOM-free + injectable (`GeolocationLike`) so it runs in Node tests; defaults to
// `navigator.geolocation` in the browser. Returns the result the mini-app gets —
// it never sees the raw Position object or any permission state.

import type { MainCapabilityHandler } from "../worker/client.ts";

export interface GeoCoords {
  latitude: number;
  longitude: number;
  accuracy: number;
}
export interface GeoPosition {
  coords: GeoCoords;
  timestamp?: number;
}
export interface GeoError {
  code: number; // 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT
  message?: string;
}
export interface GeolocationLike {
  getCurrentPosition(
    success: (pos: GeoPosition) => void,
    error: (err: GeoError) => void,
    options?: { enableHighAccuracy?: boolean; timeout?: number; maximumAge?: number },
  ): void;
}

export interface LocationResult {
  latitude: number;
  longitude: number;
  accuracy: number;
  precision: "coarse" | "fine";
}

/** ~2 decimal places ≈ 1.1 km — coarse can't resolve below a neighbourhood. */
const COARSE_DECIMALS = 2;
const COARSE_MIN_ACCURACY_M = 1100;

/** Build the location handler. Registered under BOTH `location.coarse` and
 *  `location.fine`; the op decides the precision. */
export function createLocationHandler(geolocation?: GeolocationLike): MainCapabilityHandler {
  return async (op: string): Promise<LocationResult> => {
    const geo = geolocation ?? (globalThis as { navigator?: { geolocation?: GeolocationLike } }).navigator?.geolocation;
    if (!geo) throw new Error("location unavailable on this device");
    const fine = op === "location.fine";

    const pos = await new Promise<GeoPosition>((resolve, reject) => {
      geo.getCurrentPosition(
        resolve,
        (e) => reject(new Error(geoErrorMessage(e))),
        { enableHighAccuracy: fine, timeout: 15000, maximumAge: fine ? 0 : 60000 },
      );
    });

    const { latitude, longitude, accuracy } = pos.coords;
    if (fine) {
      return { latitude, longitude, accuracy, precision: "fine" };
    }
    return {
      latitude: round(latitude, COARSE_DECIMALS),
      longitude: round(longitude, COARSE_DECIMALS),
      accuracy: Math.max(accuracy, COARSE_MIN_ACCURACY_M),
      precision: "coarse",
    };
  };
}

/**
 * The broker's answer to a mini-app, as a CODE rather than a sentence.
 *
 * This rejects into third-party JS, so it is an API surface and not UI: the
 * mini-app decides what its own user sees, and we cannot translate for it. It
 * used to return lowercase English ("location permission denied") which was
 * neither a good code nor a translatable sentence — it looked like prose to the
 * literal scanner and read like a stray string to a reviewer.
 *
 * Stable, machine-readable, and documented in the SDK.
 */
function geoErrorMessage(e: GeoError): string {
  switch (e.code) {
    case 1: return "location/permission-denied";
    case 2: return "location/unavailable";
    case 3: return "location/timeout";
    default: return e.message || "location/error";
  }
}

function round(n: number, decimals: number): number {
  const f = 10 ** decimals;
  return Math.round(n * f) / f;
}
